import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(true); // Default to dark
  const [isLoading, setIsLoading] = useState(true);

  // Load theme preference from API
  useEffect(() => {
    const fetchThemePreference = async () => {
      try {
        const response = await axios.get('/settings/theme');
        if (response.data && response.data.theme !== undefined) {
          setIsDark(response.data.theme === 'dark');
        }
      } catch (error) {
        console.error('Error fetching theme preference:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchThemePreference();
  }, []);

  // Update theme preference through API and apply theme
  useEffect(() => {
    if (!isLoading) {
      const saveThemePreference = async () => {
        try {
          await axios.post('/settings/theme', { 
            theme: isDark ? 'dark' : 'light' 
          });
        } catch (error) {
          console.error('Error saving theme preference:', error);
        }
      };

      saveThemePreference();
    }

    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark, isLoading]);

  const toggleTheme = () => {
    setIsDark(prev => !prev);
  };

  const value = {
    isDark,
    toggleTheme,
    theme: isDark ? 'dark' : 'light'
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}; 